/**
 * Category Cockpit — the shell every page renders inside: header with the four screens,
 * the sector switch (the sectors of the selected site), the Sources toggle, and the context
 * the pages read the sector from.
 *
 * The site comes from the global SiteContext (no local selector, per the admin convention);
 * the sector is resolved from sites.sector_id / sector_ids by the backend and remembered per
 * site in localStorage. "Sources" shows table names, endpoints and code citations behind every
 * number — off by default, remembered.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { fetchSiteSectors, type SiteSectors } from '@/services/categoryCockpitService';
import { TooltipHost } from '@/pages/product-cockpit/components/Tooltip';
import { HelpDrawer, type HelpTab } from '../help/Help';
import '@/pages/product-cockpit/product-cockpit.css';
import '../category-cockpit.css';

export type Screen = 'cats' | 'cat' | 'inbox' | 'journal';

export interface CockpitContextValue {
  siteId: string | null;
  siteName: string | null;
  sectorId: number | null;
  sectors: SiteSectors['sectors'];
  setSector: (id: number) => void;
  sectorLoading: boolean;
  sectorError: string | null;
  sources: boolean;
  toggleSources: () => void;
}

const CockpitContext = createContext<CockpitContextValue | null>(null);

export function useCockpit(): CockpitContextValue {
  const v = useContext(CockpitContext);
  if (!v) throw new Error('useCockpit must be used inside <Shell>');
  return v;
}

function isAbort(err: unknown): boolean {
  return (err as { name?: string })?.name === 'AbortError';
}

function readLS(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeLS(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private window, blocked storage: a convenience, not state */ }
}

export const LAST_PATH_KEY = 'kgc.lastPath';

interface ShellProps {
  active: Screen;
  /** the label of the "Category" tab, when a category is open */
  catLabel?: string | null;
  /** a line under the header, e.g. "computed 12 s ago · read replica" */
  stamp?: React.ReactNode;
  children: React.ReactNode;
}

export function Shell({ active, catLabel, stamp, children }: ShellProps) {
  const { selectedSite, loading: siteLoading } = useSite();
  const location = useLocation();
  const siteId = selectedSite?.id ?? null;
  const siteName = selectedSite?.name ?? null;

  const [sectors, setSectors] = useState<SiteSectors['sectors']>([]);
  const [sectorId, setSectorId] = useState<number | null>(null);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sectorError, setSectorError] = useState<string | null>(null);
  const [sources, setSources] = useState<boolean>(() => readLS('kgc.sources') === '1');
  const [help, setHelp] = useState<HelpTab | null>(null);

  useEffect(() => {
    if (!help) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setHelp(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [help]);

  // The resolved sector belongs to the site it was resolved FOR, so changing site drops it
  // before the new lookup starts. Keeping it meant the header named the newly selected site
  // while the screen underneath still showed the previous site's sector and its categories —
  // and a failed lookup left that stale reading in place indefinitely, with nothing saying so.
  useEffect(() => {
    setSectors([]);
    setSectorId(null);
    setSectorError(null);
    if (!siteId) { setSectorLoading(false); return undefined; }
    const ac = new AbortController();
    setSectorLoading(true);
    fetchSiteSectors(siteId, ac.signal)
      .then((r) => {
        setSectors(r.sectors);
        const remembered = Number(readLS(`kgc.sector.${siteId}`));
        const pick = r.sectors.find((s) => s.id === remembered)?.id ?? r.primary_sector_id ?? r.sectors[0]?.id ?? null;
        setSectorId(pick);
        setSectorLoading(false);
      })
      .catch((err) => {
        if (isAbort(err)) return;
        setSectorError(err?.message || String(err));
        setSectorLoading(false);
      });
    return () => ac.abort();
  }, [siteId]);

  const setSector = useCallback((id: number) => {
    setSectorId(id);
    if (siteId) writeLS(`kgc.sector.${siteId}`, String(id));
  }, [siteId]);

  const toggleSources = useCallback(() => {
    setSources((v) => { writeLS('kgc.sources', v ? '0' : '1'); return !v; });
  }, []);

  const ctx = useMemo<CockpitContextValue>(() => ({
    siteId, siteName, sectorId, sectors, setSector, sectorLoading, sectorError, sources, toggleSources,
  }), [siteId, siteName, sectorId, sectors, setSector, sectorLoading, sectorError, sources, toggleSources]);

  const lastPath = readLS(LAST_PATH_KEY);
  const catTo = lastPath ? `/category-cockpit/category?path=${encodeURIComponent(lastPath)}` : '/category-cockpit';
  const sector = sectors.find((s) => s.id === sectorId) ?? null;

  const tabs: Array<[Screen, string, string]> = [
    ['cats', 'Categories', '/category-cockpit'],
    ['cat', catLabel || (lastPath ? lastPath.split(' > ').slice(-1)[0] : 'Category'), catTo],
    ['inbox', 'To decide', '/category-cockpit/decide'],
    ['journal', 'Journal', '/category-cockpit/journal'],
  ];

  return (
    <CockpitContext.Provider value={ctx}>
      <div className={`pm-cockpit pm-kg${sources ? ' pmk-sources' : ''}`} data-screen={active} data-pathname={location.pathname}>
        <header className="pmk-top">
          <Link className="pmk-brand" to="/category-cockpit"><span className="pmk-mark" /><b>Category Cockpit</b><span className="pmk-beta" data-tip="the cockpit drives the endpoints that already own these writes; it adds none of its own">BETA · writes behind a confirmation</span></Link>
          <nav className="pmk-nav" aria-label="Category cockpit screens">
            {tabs.map(([k, label, to]) => (
              <Link key={k} to={to} className={active === k ? 'on' : ''} aria-current={active === k ? 'page' : undefined}>{label}</Link>
            ))}
          </nav>
          <div className="pmk-tools">
            {siteName && (
              <label className="pmk-sector">
                <span className="pmc-mute pmc-small">{siteName} ·</span>
                {sectors.length > 1 ? (
                  <select className="pmc-select" value={sectorId ?? ''} onChange={(e) => setSector(Number(e.target.value))} aria-label="Sector">
                    {sectors.map((s) => <option key={s.id} value={s.id}>{s.name || `sector ${s.id}`}</option>)}
                  </select>
                ) : (
                  <b>{sector ? (sector.name || `sector ${sector.id}`) : sectorLoading ? 'resolving sector…' : 'no sector'}</b>
                )}
              </label>
            )}
            <button type="button" className={`pmk-tool${help ? ' on' : ''}`} onClick={() => setHelp(help ? null : 'concepts')} aria-pressed={!!help} title="Concepts, glossary, benchmark, principles">Help</button>
            <button type="button" className={`pmk-tool${sources ? ' on' : ''}`} onClick={toggleSources} aria-pressed={sources} title="Show table names, endpoints and code citations behind every number">
              Sources <i className="pmk-switch" />
            </button>
            {stamp && <span className="pmk-stamp">{stamp}</span>}
          </div>
        </header>

        {siteLoading && <div className="pmc-empty">Loading sites…</div>}
        {!siteLoading && !siteId && (
          <div className="pmc-empty">Select a site in the header: the cockpit shows the categories of that site’s sector.</div>
        )}
        {!siteLoading && siteId && sectorError && (
          <div className="pmc-error"><div className="pmc-error-title">The site’s sectors could not be read</div><div className="pmc-error-msg">{sectorError}</div></div>
        )}
        {!siteLoading && siteId && !sectorError && sectorLoading && (
          <div className="pmc-empty pmk-loading">Resolving the sector of {siteName || 'this site'}…</div>
        )}
        {!siteLoading && siteId && !sectorError && !sectorLoading && sectorId == null && (
          <div className="pmc-empty">This site carries no sector (sites.sector_id / sector_ids are empty), so it has no category book to show.</div>
        )}
        {!siteLoading && siteId && !sectorError && !sectorLoading && sectorId != null && children}
        {help && <HelpDrawer tab={help} setTab={setHelp} onClose={() => setHelp(null)} lastPath={lastPath} />}
        <TooltipHost />
      </div>
    </CockpitContext.Provider>
  );
}
